import { useState } from 'react';
import { useAuthStore } from '../store/auth';
import { getUserDisplayName } from '../lib/user';
import { Avatar, Button, Input, Space, Typography, Popconfirm, Empty, Spin, Flex } from 'antd';
import { UserOutlined, SendOutlined, DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

interface Comment {
  id: number;
  content: string;
  createdAt: string;
  updatedAt: string;
  userId: number;
  user: {
    id: number;
    name?: string;
    username?: string;
    email?: string;
  };
}

interface CommentSectionProps {
  comments: Comment[];
  onAddComment: (content: string) => Promise<void>;
  onDeleteComment: (commentId: number) => Promise<void>;
  loading?: boolean;
}

export default function CommentSection({
  comments,
  onAddComment,
  onDeleteComment,
  loading = false,
}: CommentSectionProps) {
  const { user } = useAuthStore();
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleSubmit = async () => {
    if (!newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      await onAddComment(newComment.trim());
      setNewComment('');
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: number) => {
    if (deletingId) return;

    setDeletingId(commentId);
    try {
      await onDeleteComment(commentId);
    } catch (err) {
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Sort comments by createdAt ascending (oldest first)
  const sortedComments = [...comments].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  return (
    <div>
      <Spin spinning={loading}>
        {sortedComments.length === 0 ? (
          <Empty description="尚無評論，成為第一個留言的人吧！" />
        ) : (
          <Space orientation="vertical" style={{ width: '100%' }}>
            {sortedComments.map((comment) => (
              <Flex key={comment.id} gap={12} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Avatar
                  style={{
                    backgroundColor: comment.userId === user?.id ? '#1890ff' : '#52c41a',
                    flexShrink: 0,
                  }}
                  icon={<UserOutlined />}
                >
                  {(comment.user.name || comment.user.username || '?')[0].toUpperCase()}
                </Avatar>
                <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                  <Flex justify="space-between" align="flex-start">
                    <Space>
                      <Text strong>{getUserDisplayName(comment.user)}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatDate(comment.createdAt)}
                      </Text>
                    </Space>
                    {comment.userId === user?.id && (
                      <Popconfirm
                        title="確定要刪除此評論嗎？"
                        onConfirm={() => handleDelete(comment.id)}
                        okText="確定"
                        cancelText="取消"
                      >
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          loading={deletingId === comment.id}
                          size="small"
                        >
                          刪除
                        </Button>
                      </Popconfirm>
                    )}
                  </Flex>
                  <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{comment.content}</div>
                </Flex>
              </Flex>
            ))}
          </Space>
        )}
      </Spin>

      <div style={{ marginTop: 16 }}>
        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            placeholder="撰寫評論..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            rows={2}
            disabled={submitting}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!newComment.trim()}
          >
            發送
          </Button>
        </Space.Compact>
      </div>
    </div>
  );
}
